import React, { useEffect, useState } from "react";
import { Grid, useObserver, useRouter } from "@shoothill/core";
import MaterialTable from "material-table";
import * as Defaults from "Globals/Defaults/TableOptions";
import { DarwinTableButtonContainer, DarwinTablePageContent, DarwinTableSearchBox, DarwinTableToolbar } from "Globals/Styles/AppStyling";
import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { RDDV2ItemViewModel } from "./RDDV2ItemViewModel";
import { RDDV2ItemResponseViewModel } from "./RDDV2ItemResponseViewModel";
import { AddEditResponseView } from "./Modals/Response/AddEditResponseView";
import { RDDV2FileView } from "./Modals/Files/RDDV2FileView";
import { RDDV2StatusView } from "./Modals/Status/RDDV2StatusView";
import { styled } from "@shoothill/core";
import { CustomHr } from "../../CustomComponents";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { PrimaryButton, DownloadButton } from "Components/Buttons/Buttons";
import { pxToRem } from "Globals/Styles/AppTheme";
import { RDDV2ViewModel } from "./RDDV2ViewModel";
import { PatchedPagination } from "../../../../Components/Table/PatchedPagination";
import { TableListSearch } from "../../../../Components/Table/TableListSearch";
import { OutputIssueCheckboxView } from "../OutputTracker/Shared";
import { IssueView } from "./Issue/IssueView";

// Styling
import EditLogo from "Content/AddEdit.svg";
import DeleteLogo from "Content/Bin.svg";
import RevisionLogo from "Content/Revision.svg";
import { SolidDivider } from "Views/PurchaseOrder/Form/Views/Dividers";
import { StoresInstance } from "Globals/Stores";

interface IRDDV2TableProps {
    data: RDDV2ItemViewModel[];
    isLoading: boolean;
    handleSearchChange: (val: string) => void;
    projectId: string;
    viewModel: RDDV2ViewModel;
    onToggleIssue: (id: string) => void;
}

export const RDDV2Table: React.FunctionComponent<IRDDV2TableProps> = (props: IRDDV2TableProps) => {
    const { history } = useRouter();
    const viewModel = props.viewModel;
    const classes = useStyles();
    const [sortedState, setSortedState] = useState<RDDV2ItemViewModel[]>([]);
    const tableOptions = Defaults.GetDarwinTableOptions() as any;
    // Local state to force re-render
    const [forceRerender, setForceRerender] = useState(false);

    useEffect(() => {
        handleSortChange();
        return () => {};
    }, []);

    useEffect(() => {
        setSortedState(props.data);
        return () => {};
    });

    const setSearchString = (value: string) => {
        props.handleSearchChange(value);
    };

    // Wrap the onToggleIssue to also toggle forceRerender
    const handleToggleIssue = (id: string) => {
        if (props.onToggleIssue) {
            props.onToggleIssue(id);
            setForceRerender((prev) => !prev);
        }
    };

    const handleSortChange = (orderBy: number = 10, orderDirection: "asc" | "desc" = "asc") => {
        if (orderBy === 10) {
            if (orderDirection === "asc") {
                const sortedData = props.data.slice().sort((a: RDDV2ItemViewModel, b: RDDV2ItemViewModel) => {
                    const dateA = a.model.requestedDate ? new Date(a.model.requestedDate).getTime() : 0;
                    const dateB = b.model.requestedDate ? new Date(b.model.requestedDate).getTime() : 0;

                    return dateA - dateB;
                });
                setSortedState(sortedData);
            } else {
                const sortedData = props.data.slice().sort((a: RDDV2ItemViewModel, b: RDDV2ItemViewModel) => {
                    const dateA = a.model.requestedDate ? new Date(a.model.requestedDate).getTime() : 0;
                    const dateB = b.model.requestedDate ? new Date(b.model.requestedDate).getTime() : 0;

                    return dateB - dateA;
                });
                setSortedState(sortedData);
            }
        }
    };

    const renderPage = () => {
        return (
            <>
                <SearchBarContainer>
                    <TableListSearch placeholder={"Keyword search"} searchString={viewModel.getSearchString()} setSearchString={setSearchString} />
                    <PrimaryButton displayName="Create new" execute={() => viewModel.handleCreateNew(props.projectId)} fullWidth={false} canExecute={!props.isLoading} />
                    <PrimaryButton displayName="Import" fullWidth={false} canExecute={!props.isLoading} />
                    <PrimaryButton displayName="Export all" fullWidth={false} canExecute={!props.isLoading} execute={() => viewModel.exportAsCSV(props.projectId)} />
                    <DarwinTableButtonContainer>
                        <DownloadButton canExecute={viewModel.canIssue} color="primary" displayName="Issue" execute={() => viewModel.displayIssue(props.projectId)} />
                    </DarwinTableButtonContainer>
                </SearchBarContainer>

                <Grid padding={"15px 0px 0px 0px"}>
                    <Box flex>
                        <span className="small-title-rddV2">Total Drafts: {viewModel.getDraftCount}</span>
                        <span className="small-title-rddV2">Total Awaiting Issue: {viewModel.getAwaitingIssueCount}</span>
                        <span className="small-title-rddV2">Total Closed: {viewModel.getClosedCount}</span>
                        <span className="small-title-rddV2" hidden={true}>
                            Total Responses: {viewModel.getRDDV2ItemResponsesLength}
                        </span>
                    </Box>
                </Grid>
                <CustomHr margin={"6px 0"} padding={"0px 0px"} />
                <DarwinTablePageContent>
                    <MaterialTable
                        columns={[
                            {
                                title: "Issue",
                                field: "",
                                cellStyle: { ...tableOptions.cellStyle, width: "50px" },
                                render: (rowData: RDDV2ItemViewModel) => <OutputIssueCheckboxView toggleIssue={() => handleToggleIssue(rowData.id)} issueChecked={rowData.issue} />,
                                sorting: false,
                            },
                            {
                                title: "No. of responses",
                                field: "numberOfResponses",
                                render: (rowData: RDDV2ItemViewModel) => viewModel.handleReturnResponseCount(rowData),
                            },
                            { title: "Category", field: "categoryName" },
                            {
                                title: "Client ref.",
                                field: "clientReference",
                                render: (rowData: RDDV2ItemViewModel) => (
                                    <>
                                        <span>{rowData.clientReferenceFormatted}</span>
                                    </>
                                ),
                            },
                            { title: "Supplier", field: "supplierName" },
                            { title: "RFI Reference", field: "title" },
                            { title: "Item description", field: "description" },
                            {
                                title: "Request date",
                                field: "requestedDate",
                                render: (rowData: RDDV2ItemViewModel) => rowData.requestedDateFormatted,
                            },
                            {
                                title: "Response required by",
                                field: "responseRequiredByDate",
                                render: (rowData: RDDV2ItemViewModel) => rowData.responseRequiredByDateFormatted,
                            },
                            {
                                title: "Attchs. issued",
                                field: "numberOfFiles",
                                align: "center",
                                render: (rowData: RDDV2ItemViewModel) => (
                                    <>
                                        {rowData.numberOfFiles > 0 ? (
                                            <PrimaryButton
                                                execute={() => viewModel.handleFilesShow(null, rowData.id, props.projectId)}
                                                displayName={rowData.numberOfFiles.toString()}
                                                fullWidth={false}
                                                canExecute={!props.isLoading}
                                                stopPropagation={true}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </>
                                ),
                            },
                            {
                                title: "Latest response",
                                field: "latestResponseDate",
                                render: (rowData: RDDV2ItemViewModel) => rowData.latestResponseDateFormatted,
                            },
                            {
                                title: "Status",
                                field: "statusName",
                                defaultSort: "asc",
                                render: (rowData: RDDV2ItemViewModel) => (
                                    <>
                                        <StatusBox $viewModel={viewModel} $id={rowData.model.rddV2StatusId}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <DarwinSelect
                                                    displayName=""
                                                    stopPropagation={true}
                                                    executeWithEvent={(e, data: { id: string; displayName: string }) => viewModel.handleStatusId(e, rowData, data, props.projectId)}
                                                    fullWidth={true}
                                                    getOptionLabel={(option: any) => option.displayName}
                                                    options={viewModel.statuses}
                                                    onBlur={() => rowData.isFieldValid("rddV2StatusId")}
                                                    placeholder="Please select"
                                                    value={viewModel.handleReturnStatus(rowData)}
                                                    error={rowData.getError("rddV2StatusId") !== ""}
                                                    validationMessage={rowData.getError("rddV2StatusId")}
                                                    canExecute={StoresInstance.Domain.AccountStore.getCanEditRDDRFI}
                                                />
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "right" }}>
                                                {StoresInstance.Domain.AccountStore.getCanEditRDDRFI && (
                                                    <span
                                                        onClick={(e) => viewModel.handleRowClick(e, rowData!, props.projectId)}
                                                        style={{ paddingTop: "20px", paddingRight: "20px" }}
                                                        className={classes.inputIcon}
                                                    >
                                                        <img src={EditLogo} />
                                                    </span>
                                                )}
                                                {/* <span onClick={(e) => viewModel.handleRevisionClick(e, rowData!, props.projectId)} className={classes.inputIcon} id="revision">
                                                    <img src={RevisionLogo} />
                                                </span> */}
                                            </div>
                                        </StatusBox>
                                    </>
                                ),
                            },
                        ]}
                        detailPanel={(rowData) => {
                            return (
                                <>
                                    <ResponsePanel>
                                        <div>
                                            <span>Response ({viewModel.handleReturnResponseCount(rowData)})</span>
                                        </div>
                                        {viewModel.rddV2ItemResponseViewModels
                                            .filter((d) => d.rddV2Id === rowData.id)
                                            .map((item: RDDV2ItemResponseViewModel, index: number) => {
                                                return (
                                                    !item.model.isDeleted && (
                                                        <Box>
                                                            {item.model.reference && (
                                                                <Box>
                                                                    <ResponseItemTextContainer>
                                                                        <Typography variant="body1">
                                                                            {rowData.clientReferenceFormatted}.{item.model.reference}
                                                                        </Typography>
                                                                    </ResponseItemTextContainer>
                                                                </Box>
                                                            )}

                                                            <Box>
                                                                <ResponseItemTextContainer>
                                                                    <Typography variant="body1">{item.model.comment}</Typography>
                                                                </ResponseItemTextContainer>
                                                            </Box>
                                                            <Box>
                                                                <ResponseItemTextContainer>
                                                                    <Typography variant="body1">From {item.model.projectDirectoryPersonName}</Typography>
                                                                </ResponseItemTextContainer>
                                                            </Box>
                                                            <Box style={{ alignItems: "center" }}>
                                                                <ResponseItemTextContainer>
                                                                    <Typography variant="body1">Date received - {item.model.receivedDateFormatted}</Typography>
                                                                    <ResponseItemTextContainer>
                                                                        <Typography variant="body1">
                                                                            Created: {item.model.createdByName} on {item.model.createdDateFormatted}
                                                                        </Typography>
                                                                    </ResponseItemTextContainer>
                                                                    <ResponseItemTextContainer>
                                                                        {item.model.lastUpdatedByName == null && <Typography variant="body1">Last updated: N/A</Typography>}
                                                                        {item.model.lastUpdatedByName != null && (
                                                                            <Typography variant="body1">
                                                                                Updated: {item.model.lastUpdatedByName} on {item.model.lastUpdatedDateFormatted}
                                                                            </Typography>
                                                                        )}
                                                                    </ResponseItemTextContainer>
                                                                    <ResponseItemTextContainer>
                                                                        <Typography variant="body1">{`Status Changed to ${item.model.statusName}`}</Typography>
                                                                    </ResponseItemTextContainer>
                                                                    <ResponseItemTextContainer>
                                                                        {item.model.responseRequiredByFormatted && (
                                                                            <Typography variant="body1">
                                                                                {`Response required updated to ${item.model.responseRequiredByFormatted}`}
                                                                            </Typography>
                                                                        )}
                                                                    </ResponseItemTextContainer>
                                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "right" }}>
                                                                        {StoresInstance.Domain.AccountStore.getCanEditRDDRFI && (
                                                                            <>
                                                                                <span
                                                                                    onClick={() => viewModel.handleAddResponseShow(item.id, rowData.id, props.projectId)}
                                                                                    style={{ paddingRight: "20px" }}
                                                                                    className={classes.inputIcon}
                                                                                >
                                                                                    <img src={EditLogo} />
                                                                                </span>
                                                                                <span className={classes.inputIcon}>
                                                                                    <img src={DeleteLogo} />
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </ResponseItemTextContainer>
                                                            </Box>
                                                            <Box>
                                                                <SolidDivider gutterBottom={true} borderThickness={1} borderOpacity={1} />
                                                            </Box>
                                                        </Box>
                                                    )
                                                );
                                            })}
                                        <Box>
                                            <PrimaryButton
                                                execute={() => viewModel.handleAddResponseShow(null, rowData.id, props.projectId)}
                                                displayName="Add response"
                                                fullWidth={false}
                                                canExecute={!props.isLoading}
                                            />
                                        </Box>
                                    </ResponsePanel>
                                </>
                            );
                        }}
                        onOrderChange={(orderBy, orderDirection) => handleSortChange(orderBy, orderDirection)}
                        components={{
                            Pagination: PatchedPagination,
                        }}
                        options={Defaults.GetDarwinTableOptionsNoSearchV2()}
                        data={sortedState}
                        isLoading={props.isLoading}
                        onRowClick={(e, rowData) => {
                            viewModel.handleRowClick(e, rowData!, props.projectId);
                        }}
                        title=""
                    />
                </DarwinTablePageContent>
                {viewModel.addResponse && (
                    <AddEditResponseView
                        rddV2Id={viewModel.rddV2Id}
                        id={viewModel.rddV2ResponseId}
                        open={viewModel.addResponse}
                        onClose={() => viewModel.handleAddResponseClose(props.projectId)}
                    />
                )}
                {/* {viewModel.showItems && <DesignDataItemView designDataId={viewModel.designDataId} open={viewModel.showItems} onClose={() => viewModel.handleShowItemsClose()} />} */}
                {viewModel.showFiles && <RDDV2FileView rddV2Id={viewModel.rddV2Id} open={viewModel.showFiles} onClose={() => viewModel.handleShowFilesClose()} />}
                {viewModel.showStatusChange && (
                    <RDDV2StatusView
                        rddV2Id={viewModel.rddV2Id}
                        open={viewModel.showStatusChange}
                        onClose={() => viewModel.handleStatusChangeClose()}
                        onYes={() => viewModel.handleStatusChangeClose()}
                    />
                )}
                {viewModel.canDisplayIssue && <IssueView viewModel={viewModel.issueViewModel!} />}
            </>
        );
    };

    return useObserver(() => renderPage());
};

const StatusBox = styled(Box)<{ $viewModel: RDDV2ViewModel; $id: string }>`
    .MuiFormControl-root {
        .MuiInputBase-root {
            background-color: ${(props) => props.$viewModel.setBackGroundStatus(props.$id)} !important;
            color: ${(props) => props.$viewModel.setColorStatus(props.$id)} !important;
        }
    }
`;

export const ResponsePanel: any = styled.li`
    position: relative;
    list-style-type: none;
    padding-left: 40px;
    padding-right: 40px;
    padding-top: 15px;
    margin-bottom: 10px;

    > div {
        font-size: 12px;
    }

    > div > span {
        font-weight: bold;
    }
`;

export const ResponseItemTextContainer = styled.div`
    margin-top: 10px;
    margin-bottom: 10px;
    padding-right: 0px;

    p {
        font-size: 12px !important;
    }
`;

export const ClientReference = styled.span`
    text-decoration: underline !important;
    color: blue !important;
`;

export const SearchBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 20px 0px 10px;

    > div:nth-child(1) {
        max-width: 50%;
        width: 100%;
    }

    > button {
        width: auto;
        white-space: nowrap;
        min-width: 165px;
        margin-left: 30px;

        span {
            font-size: ${pxToRem(12)} !important;
        }
    }
`;

export const SubTitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0px 30px 10px;

    > p {
        font-size: 18px;
        letter-spacing: -0.36px;
        font-weight: 600;
    }

    > div {
        button {
            margin-bottom: 8px;
        }

        p {
            font-size: 8px;
            font-weight: 600;
            line-height: 11px;
            letter-spacing: -0.16px;
        }
    }
`;

const useStyles = makeStyles({
    buttonGroup: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    autoCompleteInput: {
        display: "flex",
        justifyContent: "start",
        paddingRight: "30px !important",
        paddingLeft: "40px !important",
        "& div.MuiAutocomplete-inputRoot": {
            width: "90%",
            height: "90%",
            display: "flex",
            alignItems: "center",
        },
    },
    input: {
        "& .MuiInputBase-root": {
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
            margin: "0 5px",
            height: "90%",
            backgroundColor: "gold",
            width: "90%",

            "& > input": {
                textAlign: "right",
                fontSize: "12px",
            },
        },
    },
    inputIcon: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "20px",
    },
});
